/* Complete bilingual content for Intake forms - 100% coverage */

export const INTAKE_CONTENT = {
  en: {
    // IntakeStart page
    start: {
      expiredBanner: "Your session expired. Please retake the intake form.",
      eyebrow: "Eligibility check",
      title: "What are you interested in?",
      subtitle: "Pick a program below to start your eligibility check. It takes about 3 minutes.",
    },

    // Programs for picker
    programs: {
      "tirzepatide-core": { name: "Tirzepatide Core Program", desc: "Start controlling your appetite and begin structured weight loss." },
      "metabolic-boost": { name: "Metabolic Boost Program", desc: "Weight loss + energy + mental clarity.", badge: "Most popular" },
      "total-body-optimization": { name: "Total Body Optimization", desc: "Complete transformation: weight, recovery & antiaging." },
      "advanced-transformation": { name: "Advanced Transformation", desc: "Maximum results for serious transformation." },
    },

    // Common elements
    common: {
      loading: "Loading…",
      continue: "Continue",
      back: "Back",
      getStarted: "Get Started",
      optional: "(optional)",
      seeIfEligible: "See If You're Eligible",
      clickToContinue: "Click to Continue",
      required: "required",
      yes: "Yes",
      no: "No",
      other: "Other",
      preferNotToSay: "Prefer not to say",
    },

    // Trust pillars
    trust: [
      "FDA-approved formulations sourced through licensed pharmacies",
      "Personalized protocols based on your goals and history",
      "Effective results with ongoing physician supervision",
    ],

    // Validation messages
    validation: {
      selectState: "Please select your state",
      bothNamesRequired: "Both names are required",
      validEmail: "Please enter a valid email",
      enterDob: "Please enter your date of birth",
      mustBe18: "Must be 18 or older.",
      heightWeightRequired: "Height and current weight are required",
      pickOption: "Please pick an option",
      shareDetails: "Please share a few details",
      selectAtLeastOne: "Please select at least one option",
      pickDose: "Please pick your dose",
    },

    // Intro slides
    glp1Intro: {
      eyebrow: "GLP-1 Weight Loss",
      title: "1 in 8 Americans use GLP-1 to lose weight",
      subtitle: "We'll help you get started with proven medication and online care.",
    },

    eligibilityConfirmation: {
      eyebrow: "Great News!",
      title: "Based on this information, you may be eligible for GLP-1 treatment",
      subtitle: "Let's find the best option for your goals.",
    },

    goodNews: {
      eyebrow: "Good news!",
      title: "You may be eligible for treatment",
      body: "Based on your information, you may be eligible for GLP-1 treatment. Let's find the best option for your goals.",
    },

    greatChoice: {
      title: "Great choice!",
      loading: "Loading your personalized questions...",
    },

    // All question steps
    steps: {
      state: {
        title: "Where are we shipping to?",
        subtitle: "Your state determines pricing, available programs, and shipping. All 50 states served.",
        placeholder: "Select your state…",
      },
      name: {
        title: "Let's get you started",
        subtitle: "Your medical team will use this to personalize and confirm your treatment plan.",
        firstName: "First name",
        lastName: "Last name",
        email: "Email (we'll send your account info here)",
      },
      dob: {
        title: "When were you born?",
        subtitle: "You must be 18 or older to enroll.",
      },
      gender: {
        title: "What is your sex assigned at birth?",
        subtitle: "This helps us determine the safest treatment for you.",
        male: "Male",
        female: "Female",
        preferNotToSay: "Prefer not to say",
      },
      heightWeight: {
        title: "What's your current height and weight?",
        subtitle: "Helps us calculate the most effective protocol for you.",
        feet: "Feet",
        inches: "Inches",
        currentWeight: "Current weight (lbs)",
        goalWeight: "Goal weight (lbs, optional)",
        bmi: "Your BMI:",
      },
      motivation: {
        title: "What inspires you to make this change?",
        subtitle: "Select all that apply.",
        confident: "I want to feel more confident",
        energy: "I want to have more energy",
        health: "I want to improve my health",
        active: "I want to be more active",
        feelGood: "I want to feel good every day",
      },
      weightLossGoal: {
        title: "What's your weight loss goal?",
        "1_15": "1–15 lbs.",
        "16_50": "16–50 lbs.",
        "51_plus": "51 or more lbs.",
        unsure: "I'm not sure yet — I just want to get started",
      },
      weightLossAttempts: {
        title: "What ways have you tried to lose weight in the past?",
        subtitle: "Select all that apply.",
        countingCalories: "Counting calories",
        commercialPrograms: "Commercial weight loss programs or apps",
        lowCarbKeto: "Low-carb, keto, or similar diets",
        intermittentFasting: "Intermittent fasting",
        exercise: "Exercise",
        other: "Other",
      },
      bariatric: {
        title: "Have you had any of the following bariatric procedures?",
        lapBand: "Lap band",
        gastricSleeve: "Gastric sleeve",
        gastricBypass: "Gastric bypass",
        none: "None of these",
      },
      priorSurgery: {
        title: "Have you ever had any surgeries or hospitalizations?",
        detailLabel: "Please provide details",
      },
      currentGlp1: {
        title: "Are you currently taking GLP-1 medication?",
        subtitle: "Such as Ozempic, Wegovy, Mounjaro, Zepbound, or compounded GLP-1.",
      },
      whichGlp1: {
        title: "Which medication are you taking?",
      },
      doseKnown: {
        title: "Do you know your weekly dose in milligrams (mg)?",
      },
      specificDose: {
        title: "What dose are you taking?",
      },
      volumeConcentration: {
        title: "Do you know BOTH the volume (mL or units) you inject AND the concentration (mg/mL) of medication you inject?",
      },
      previouslyTakenGlp1: {
        title: "Have you previously taken GLP-1 medication?",
      },
      pastGlp1Meds: {
        title: "Which medications have you taken in the past?",
        subtitle: "Select all that apply.",
        textLabel: "Anything else not listed above? (optional)",
      },
      pregnancy: {
        title: "Are you pregnant or breastfeeding?",
        subtitle: "Or planning to become pregnant in the next 12 months.",
        notApplicable: "Not applicable",
      },
      ethnicity: {
        title: "What is your ethnicity?",
        subtitle: "We ask this to better tailor treatment options to you.",
        africanAmerican: "African American",
        caucasian: "Caucasian",
        hispanicLatin: "Hispanic or Latin",
        nativeAmerican: "Native American or Alaskan",
        pacificIslander: "Pacific Islander",
        asian: "Asian",
        other: "Other",
        preferNotToSay: "Prefer not to say",
      },
      recreational: {
        title: "Do you use recreational drugs?",
        subtitle: "To ensure your safety, please answer truthfully. Your information is confidential and reviewed only by your medical team.",
        detailLabel: "Which ones? (Please specify)",
      },
      currentConditions: {
        title: "Are you currently being treated for any of the following conditions?",
        subtitle: "This information helps us ensure treatment is safe for you. Select all that apply.",
        activeCancer: "Active cancer or tumor (excluding non-melanoma skin cancer)",
        activeGallbladder: "Active gallbladder disease",
        allergyGlp1: "Allergy or hypersensitivity to GLP-1 medication",
        bowelObstruction: "Bowel obstruction",
        currentDiabeticDrugs: "Current use of diabetic drugs (sulfonylureas, meglitinides, insulin, or DPP-4 inhibitors)",
        diabetes: "Diabetes (type 1, type 2, or unknown)",
        eatingDisorder: "Eating disorder in the last 12 months",
        endocrineHormone: "Endocrine or hormone disorder (e.g. pituitary, adrenal)",
        kidneyTransplant: "Kidney transplant",
        medullaryThyroid: "Medullary thyroid cancer (personal or family)",
        multipleEndocrine: "Multiple endocrine neoplasia (MEN) (personal or family)",
        pancreatitis: "Pancreatitis (history)",
        severeLiver: "Severe liver disease (cirrhosis, fibrosis, or transplantation)",
        suicidalThoughts: "Suicidal thoughts or suicide attempts in the last 12 months",
        none: "None of the above",
      },
      weightRelated: {
        title: "Have you ever been diagnosed with any of the following health conditions related to weight?",
        highBloodPressure: "High blood pressure (hypertension)",
        highCholesterol: "High cholesterol (hyperlipidemia)",
        preDiabetes: "Pre-diabetes",
        metabolicSyndrome: "Metabolic syndrome",
        osteoarthritis: "Osteoarthritis",
        cardiovascular: "Cardiovascular disease (ASCVD)",
        none: "None of the above",
      },
      otherMeds: {
        title: "Do you currently take any medications, herbal remedies, or supplements?",
        no: "No, I'm not taking any",
        yes: "Yes",
        detailLabel: "Please list them",
      },
      otherConditions: {
        title: "Any other medical conditions to share with your healthcare team?",
        detailLabel: "Tell us more",
      },
      hypothyroidism: {
        title: "Do you have untreated hypothyroidism?",
        subtitle: "A diagnosis you have not yet started treatment for.",
      },
      intracranial: {
        title: "Do you have a history of intracranial lesions?",
        subtitle: "Any tumors, cysts, or other lesions inside the skull.",
      },
      hypersensitivity: {
        title: "Do you have any hypersensitivity to sermorelin?",
        subtitle: "A known allergic or adverse reaction to sermorelin or growth hormone–releasing peptides.",
      },
      nadHistory: {
        title: "Have you previously used NAD+ or NAD+ precursors?",
        subtitle: "Such as NAD+ injections, IV therapy, or NMN/NR supplements.",
      },
      nadSensitivity: {
        title: "Do you have any known sensitivity or allergic reactions to NAD+ or its components?",
      },
    },

    // GLP-1 Medication names (brand names stay the same)
    medications: {
      wegovy: "Wegovy",
      ozempic: "Ozempic",
      rybelsus: "Rybelsus",
      compounded_semaglutide: "Compounded semaglutide",
      zepbound: "Zepbound",
      mounjaro: "Mounjaro",
      compounded_tirzepatide: "Compounded tirzepatide",
      victoza: "Victoza",
      saxenda: "Saxenda",
      compounded_liraglutide: "Compounded liraglutide",
      trulicity: "Trulicity",
      byetta: "Byetta",
    },

    // Past GLP-1 options
    pastMeds: {
      semaglutide: "Semaglutide (Wegovy / Ozempic / Rybelsus / compounded)",
      tirzepatide: "Tirzepatide (Zepbound / Mounjaro / compounded)",
      liraglutide: "Liraglutide (Saxenda / Victoza / compounded)",
      trulicity: "Trulicity",
      exenatide: "Exenatide (Byetta / Bydureon)",
    },
  },

  es: {
    // IntakeStart page
    start: {
      expiredBanner: "Tu sesión expiró. Por favor vuelve a completar el formulario.",
      eyebrow: "Verificación de elegibilidad",
      title: "¿En qué estás interesado?",
      subtitle: "Elige un programa a continuación para comenzar tu verificación de elegibilidad. Toma aproximadamente 3 minutos.",
    },

    // Programs for picker
    programs: {
      "tirzepatide-core": { name: "Programa Core de Tirzepatida", desc: "Comienza a controlar tu apetito e inicia la pérdida de peso estructurada." },
      "metabolic-boost": { name: "Programa de Impulso Metabólico", desc: "Pérdida de peso + energía + claridad mental.", badge: "Más popular" },
      "total-body-optimization": { name: "Optimización Corporal Total", desc: "Transformación completa: peso, recuperación y anti-aging." },
      "advanced-transformation": { name: "Transformación Avanzada", desc: "Resultados máximos para transformación seria." },
    },

    // Common elements
    common: {
      loading: "Cargando…",
      continue: "Continuar",
      back: "Atrás",
      getStarted: "Comenzar",
      optional: "(opcional)",
      seeIfEligible: "Ver si Eres Elegible",
      clickToContinue: "Haz Clic para Continuar",
      required: "requerido",
      yes: "Sí",
      no: "No",
      other: "Otro",
      preferNotToSay: "Prefiero no decir",
    },

    // Trust pillars
    trust: [
      "Formulaciones aprobadas por la FDA obtenidas a través de farmacias autorizadas",
      "Protocolos personalizados basados en tus objetivos e historial",
      "Resultados efectivos con supervisión médica continua",
    ],

    // Validation messages
    validation: {
      selectState: "Por favor selecciona tu estado",
      bothNamesRequired: "Se requieren ambos nombres",
      validEmail: "Por favor ingresa un correo electrónico válido",
      enterDob: "Por favor ingresa tu fecha de nacimiento",
      mustBe18: "Debes tener 18 años o más.",
      heightWeightRequired: "Se requiere estatura y peso actual",
      pickOption: "Por favor elige una opción",
      shareDetails: "Por favor comparte algunos detalles",
      selectAtLeastOne: "Por favor selecciona al menos una opción",
      pickDose: "Por favor elige tu dosis",
    },

    // Intro slides
    glp1Intro: {
      eyebrow: "Pérdida de Peso GLP-1",
      title: "1 de cada 8 estadounidenses usa GLP-1 para perder peso",
      subtitle: "Te ayudaremos a comenzar con medicación comprobada y atención en línea.",
    },

    eligibilityConfirmation: {
      eyebrow: "¡Buenas Noticias!",
      title: "Según esta información, puedes ser elegible para tratamiento GLP-1",
      subtitle: "Encontremos la mejor opción para tus objetivos.",
    },

    goodNews: {
      eyebrow: "¡Buenas noticias!",
      title: "Puedes ser elegible para tratamiento",
      body: "Según tu información, puedes ser elegible para tratamiento GLP-1. Encontremos la mejor opción para tus objetivos.",
    },

    greatChoice: {
      title: "¡Excelente elección!",
      loading: "Cargando tus preguntas personalizadas...",
    },

    // All question steps
    steps: {
      state: {
        title: "¿A dónde enviamos?",
        subtitle: "Tu estado determina el precio, los programas disponibles y el envío. Servimos los 50 estados.",
        placeholder: "Selecciona tu estado…",
      },
      name: {
        title: "Comencemos",
        subtitle: "Tu equipo médico usará esto para personalizar y confirmar tu plan de tratamiento.",
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo electrónico (enviaremos la información de tu cuenta aquí)",
      },
      dob: {
        title: "¿Cuándo naciste?",
        subtitle: "Debes tener 18 años o más para inscribirte.",
      },
      gender: {
        title: "¿Cuál es tu sexo asignado al nacer?",
        subtitle: "Esto nos ayuda a determinar el tratamiento más seguro para ti.",
        male: "Masculino",
        female: "Femenino",
        preferNotToSay: "Prefiero no decir",
      },
      heightWeight: {
        title: "¿Cuál es tu estatura y peso actual?",
        subtitle: "Nos ayuda a calcular el protocolo más efectivo para ti.",
        feet: "Pies",
        inches: "Pulgadas",
        currentWeight: "Peso actual (lbs)",
        goalWeight: "Peso deseado (lbs, opcional)",
        bmi: "Tu IMC:",
      },
      motivation: {
        title: "¿Qué te inspira a hacer este cambio?",
        subtitle: "Selecciona todas las que apliquen.",
        confident: "Quiero sentirme más seguro/a",
        energy: "Quiero tener más energía",
        health: "Quiero mejorar mi salud",
        active: "Quiero ser más activo/a",
        feelGood: "Quiero sentirme bien todos los días",
      },
      weightLossGoal: {
        title: "¿Cuál es tu meta de pérdida de peso?",
        "1_15": "1–15 libras",
        "16_50": "16–50 libras",
        "51_plus": "51 o más libras",
        unsure: "No estoy seguro/a aún — solo quiero comenzar",
      },
      weightLossAttempts: {
        title: "¿De qué formas has intentado perder peso en el pasado?",
        subtitle: "Selecciona todas las que apliquen.",
        countingCalories: "Contar calorías",
        commercialPrograms: "Programas comerciales de pérdida de peso o aplicaciones",
        lowCarbKeto: "Dietas bajas en carbohidratos, keto o similares",
        intermittentFasting: "Ayuno intermitente",
        exercise: "Ejercicio",
        other: "Otro",
      },
      bariatric: {
        title: "¿Has tenido alguno de los siguientes procedimientos bariátricos?",
        lapBand: "Banda gástrica",
        gastricSleeve: "Manga gástrica",
        gastricBypass: "Bypass gástrico",
        none: "Ninguno de estos",
      },
      priorSurgery: {
        title: "¿Alguna vez has tenido cirugías u hospitalizaciones?",
        detailLabel: "Por favor proporciona detalles",
      },
      currentGlp1: {
        title: "¿Estás tomando actualmente medicación GLP-1?",
        subtitle: "Como Ozempic, Wegovy, Mounjaro, Zepbound o GLP-1 compuesto.",
      },
      whichGlp1: {
        title: "¿Qué medicamento estás tomando?",
      },
      doseKnown: {
        title: "¿Conoces tu dosis semanal en miligramos (mg)?",
      },
      specificDose: {
        title: "¿Qué dosis estás tomando?",
      },
      volumeConcentration: {
        title: "¿Conoces TANTO el volumen (mL o unidades) que inyectas COMO la concentración (mg/mL) del medicamento que inyectas?",
      },
      previouslyTakenGlp1: {
        title: "¿Has tomado medicación GLP-1 anteriormente?",
      },
      pastGlp1Meds: {
        title: "¿Qué medicamentos has tomado en el pasado?",
        subtitle: "Selecciona todos los que apliquen.",
        textLabel: "¿Algo más que no esté en la lista? (opcional)",
      },
      pregnancy: {
        title: "¿Estás embarazada o amamantando?",
        subtitle: "O planeas quedar embarazada en los próximos 12 meses.",
        notApplicable: "No aplica",
      },
      ethnicity: {
        title: "¿Cuál es tu etnia?",
        subtitle: "Preguntamos esto para adaptar mejor las opciones de tratamiento para ti.",
        africanAmerican: "Afroamericano",
        caucasian: "Caucásico",
        hispanicLatin: "Hispano o Latino",
        nativeAmerican: "Nativo Americano o de Alaska",
        pacificIslander: "Isleño del Pacífico",
        asian: "Asiático",
        other: "Otro",
        preferNotToSay: "Prefiero no decir",
      },
      recreational: {
        title: "¿Usas drogas recreativas?",
        subtitle: "Para garantizar tu seguridad, por favor responde con sinceridad. Tu información es confidencial y revisada solo por tu equipo médico.",
        detailLabel: "¿Cuáles? (Por favor especifica)",
      },
      currentConditions: {
        title: "¿Estás siendo tratado actualmente por alguna de las siguientes condiciones?",
        subtitle: "Esta información nos ayuda a garantizar que el tratamiento sea seguro para ti. Selecciona todas las que apliquen.",
        activeCancer: "Cáncer activo o tumor (excluyendo cáncer de piel no melanoma)",
        activeGallbladder: "Enfermedad activa de la vesícula biliar",
        allergyGlp1: "Alergia o hipersensibilidad a medicación GLP-1",
        bowelObstruction: "Obstrucción intestinal",
        currentDiabeticDrugs: "Uso actual de medicamentos para diabetes (sulfonilureas, meglitinidas, insulina o inhibidores DPP-4)",
        diabetes: "Diabetes (tipo 1, tipo 2 o desconocido)",
        eatingDisorder: "Trastorno alimentario en los últimos 12 meses",
        endocrineHormone: "Trastorno endocrino u hormonal (ej. pituitario, suprarrenal)",
        kidneyTransplant: "Trasplante de riñón",
        medullaryThyroid: "Cáncer medular de tiroides (personal o familiar)",
        multipleEndocrine: "Neoplasia endocrina múltiple (MEN) (personal o familiar)",
        pancreatitis: "Pancreatitis (historial)",
        severeLiver: "Enfermedad hepática grave (cirrosis, fibrosis o trasplante)",
        suicidalThoughts: "Pensamientos suicidas o intentos de suicidio en los últimos 12 meses",
        none: "Ninguno de los anteriores",
      },
      weightRelated: {
        title: "¿Alguna vez has sido diagnosticado con alguna de las siguientes condiciones de salud relacionadas con el peso?",
        highBloodPressure: "Presión arterial alta (hipertensión)",
        highCholesterol: "Colesterol alto (hiperlipidemia)",
        preDiabetes: "Prediabetes",
        metabolicSyndrome: "Síndrome metabólico",
        osteoarthritis: "Osteoartritis",
        cardiovascular: "Enfermedad cardiovascular (ASCVD)",
        none: "Ninguno de los anteriores",
      },
      otherMeds: {
        title: "¿Actualmente tomas algún medicamento, remedio herbal o suplemento?",
        no: "No, no estoy tomando ninguno",
        yes: "Sí",
        detailLabel: "Por favor enuméralos",
      },
      otherConditions: {
        title: "¿Alguna otra condición médica que compartir con tu equipo de atención médica?",
        detailLabel: "Cuéntanos más",
      },
      hypothyroidism: {
        title: "¿Tienes hipotiroidismo no tratado?",
        subtitle: "Un diagnóstico para el que aún no has comenzado tratamiento.",
      },
      intracranial: {
        title: "¿Tienes historial de lesiones intracraneales?",
        subtitle: "Cualquier tumor, quiste u otras lesiones dentro del cráneo.",
      },
      hypersensitivity: {
        title: "¿Tienes alguna hipersensibilidad a la sermorelina?",
        subtitle: "Una reacción alérgica o adversa conocida a la sermorelina o péptidos liberadores de hormona de crecimiento.",
      },
      nadHistory: {
        title: "¿Has usado previamente NAD+ o precursores de NAD+?",
        subtitle: "Como inyecciones de NAD+, terapia IV o suplementos NMN/NR.",
      },
      nadSensitivity: {
        title: "¿Tienes alguna sensibilidad conocida o reacciones alérgicas a NAD+ o sus componentes?",
      },
    },

    // GLP-1 Medication names (brand names stay the same)
    medications: {
      wegovy: "Wegovy",
      ozempic: "Ozempic",
      rybelsus: "Rybelsus",
      compounded_semaglutide: "Semaglutida compuesta",
      zepbound: "Zepbound",
      mounjaro: "Mounjaro",
      compounded_tirzepatide: "Tirzepatida compuesta",
      victoza: "Victoza",
      saxenda: "Saxenda",
      compounded_liraglutide: "Liraglutida compuesta",
      trulicity: "Trulicity",
      byetta: "Byetta",
    },

    // Past GLP-1 options
    pastMeds: {
      semaglutide: "Semaglutida (Wegovy / Ozempic / Rybelsus / compuesta)",
      tirzepatide: "Tirzepatida (Zepbound / Mounjaro / compuesta)",
      liraglutide: "Liraglutida (Saxenda / Victoza / compuesta)",
      trulicity: "Trulicity",
      exenatide: "Exenatida (Byetta / Bydureon)",
    },
  },
};

export function getIntakeContent(lang) {
  return INTAKE_CONTENT[lang] || INTAKE_CONTENT.en;
}
